import { Injectable, signal } from '@angular/core';

export type ToastKind = 'info' | 'success' | 'warning' | 'error';

export interface ToastAction {
  label: string;
  run: () => void;
}

export interface Toast {
  id: number;
  kind: ToastKind;
  title: string;
  description?: string;
  createdAt: number;
  /** `0` means the toast never auto-dismisses — the user must dismiss it or trigger its action. */
  durationMs: number;
  action?: ToastAction;
}

export interface ShowToastOptions {
  title: string;
  description?: string;
  /** `0` disables auto-dismiss. Omitted → the default duration. */
  durationMs?: number;
  action?: ToastAction;
}

const DEFAULT_DURATION = 4000;
const DEDUPE_WINDOW_MS = 1000;

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly _toasts = signal<Toast[]>([]);
  readonly toasts = this._toasts.asReadonly();

  private nextId = 1;
  private readonly recent = new Map<string, number>();

  show(kind: ToastKind, opts: ShowToastOptions): number | null {
    const key = `${kind}:${opts.title}:${opts.description ?? ''}`;
    const now = Date.now();
    const last = this.recent.get(key);
    if (last && now - last < DEDUPE_WINDOW_MS) {
      return null;
    }
    this.recent.set(key, now);

    const toast: Toast = {
      id: this.nextId++,
      kind,
      title: opts.title,
      description: opts.description,
      createdAt: now,
      durationMs: opts.durationMs ?? DEFAULT_DURATION,
      action: opts.action,
    };
    this._toasts.update((list) => [...list, toast]);
    if (toast.durationMs > 0) {
      setTimeout(() => this.dismiss(toast.id), toast.durationMs);
    }
    return toast.id;
  }

  info(opts: ShowToastOptions) {
    return this.show('info', opts);
  }

  success(opts: ShowToastOptions) {
    return this.show('success', opts);
  }

  warning(opts: ShowToastOptions) {
    return this.show('warning', opts);
  }

  error(opts: ShowToastOptions) {
    return this.show('error', opts);
  }

  dismiss(id: number) {
    this._toasts.update((list) => list.filter((t) => t.id !== id));
  }

  clear() {
    this._toasts.set([]);
  }
}
